import React, {useState, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  ScrollView,
  Image,
  Platform,
} from 'react-native';
import {useRouting} from '../hooks/useRouting';
import {useTranslation} from 'react-i18next';
import {colors, spacing, borderRadius, touchTargets} from '../constants';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {useAppDispatch, useAppSelector} from '../store';
import {createAccount, clearError} from '../store/authSlice';
import {getDeviceInfo} from '../utils/deviceInfo';
import {useDocumentMeta} from '../hooks/useDocumentMeta';
import {authService} from '../services';
import LegalInfoModal from '../components/LegalInfoModal';
import PrivacyBanner from '../components/PrivacyBanner';
import {Icon} from '../components/Icon';
import {KeyboardAware} from '../components/KeyboardAware';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

const BACKGROUND_CHANGE_INTERVAL = 30 * 60 * 1000; // 30 minutes

// Curated picsum photo IDs - warm, personal-feeling images
// (nature, cozy scenes, soft lighting, flowers, landscapes)
const CURATED_PHOTO_IDS = [
  15,
  16,
  28,
  39,
  42,
  54,
  65,
  68,
  71,
  100,
  103,
  110,
  128,
  129,
  142,
  164,
  167,
  193,
  206,
  211,
  244,
  301,
  308,
  318,
  325,
  349,
  392,
  397,
  398,
  399,
  401,
  447,
  452,
];

function getBackgroundPhoto(): string {
  const idKey = 'homeBackgroundPhotoId';
  const timestampKey = 'homeBackgroundTimestamp';

  // On native, just pick a random photo (no localStorage)
  if (Platform.OS !== 'web') {
    const randomId = CURATED_PHOTO_IDS[Math.floor(Math.random() * CURATED_PHOTO_IDS.length)];
    return `https://picsum.photos/id/${randomId}/1920/1080`;
  }

  try {
    const storedId = localStorage.getItem(idKey);
    const storedTimestamp = localStorage.getItem(timestampKey);
    const now = Date.now();

    if (storedId && storedTimestamp) {
      const elapsed = now - parseInt(storedTimestamp, 10);
      if (elapsed < BACKGROUND_CHANGE_INTERVAL) {
        return `https://picsum.photos/id/${storedId}/1920/1080`;
      }
    }

    // Select random photo from curated list
    const newId = CURATED_PHOTO_IDS[Math.floor(Math.random() * CURATED_PHOTO_IDS.length)];
    localStorage.setItem(idKey, String(newId));
    localStorage.setItem(timestampKey, String(now));
    return `https://picsum.photos/id/${newId}/1920/1080`;
  } catch {
    // Fallback with first curated photo
    return `https://picsum.photos/id/${CURATED_PHOTO_IDS[0]}/1920/1080`;
  }
}

export default function HomeScreen() {
  const {t, i18n} = useTranslation();
  useDocumentMeta(
    t('seo.title'),
    t('seo.description'),
    i18n.language,
    '/',
  );
  const {navigateTo} = useRouting();
  const insets = useSafeAreaInsets();
  const dispatch = useAppDispatch();
  const {isLoading, error} = useAppSelector(state => state.auth);
  const [showLoginOptions, setShowLoginOptions] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [backgroundPhoto] = useState(getBackgroundPhoto);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState<'faq' | 'impressum' | 'privacy' | 'terms'>('faq');
  const scrollViewRef = useRef<ScrollView>(null);

  const handleStartNew = async () => {
    dispatch(clearError());
    dispatch(createAccount(getDeviceInfo()));
  };

  const handleSendMagicLink = async () => {
    if (!email.trim()) {
      setEmailError(t('home.pleaseEnterEmail'));
      return;
    }

    setIsSendingEmail(true);
    setEmailError(null);

    try {
      await authService.requestMagicLink({email: email.trim()});
      setEmailSent(true);
    } catch (err: any) {
      setEmailError(err.message || t('home.failedToSendEmail'));
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleBackToStart = () => {
    setShowLoginOptions(false);
    setShowEmailInput(false);
    setEmailSent(false);
    setEmail('');
    setEmailError(null);
  };

  return (
    <KeyboardAware>
    <View style={styles.container}>
      {/* Background Image */}
      <Image
        source={{uri: backgroundPhoto}}
        style={styles.backgroundImage}
        resizeMode="cover"
      />

      {/* Gradient Overlay */}
      <View style={styles.gradientOverlay} />

      {/* Main Content */}
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>

        {/* Header */}
        <View style={[styles.header, {paddingTop: Math.max(insets.top, spacing.lg)}]}>
          <View style={styles.logoContainer}>
            <Image
              source={Platform.OS === 'web' ? {uri: '/icon.png'} : require('../assets/icon.png')}
              style={styles.logoImage}
              resizeMode="contain"
            />
            <Text style={styles.logoText}>{t('home.appName')}</Text>
          </View>
          <TouchableOpacity
            style={styles.helpButton}
            onPress={() => {
              setLegalModalTab('faq');
              setShowLegalModal(true);
            }}
            activeOpacity={0.8}
            accessibilityLabel={t('common.help')}
            accessibilityRole="button">
            <Text style={styles.helpButtonText}>?</Text>
          </TouchableOpacity>
        </View>

        {/* Center Card */}
        <View style={styles.cardContainer}>
          <View style={styles.card}>
            {!showLoginOptions && !showEmailInput && !emailSent && (
              <>
                <Text style={styles.welcomeTitle}>{t('home.welcomeTitle')}</Text>
                <Text style={styles.subtitle}>{t('home.subtitle')}</Text>

                {error && <Text style={styles.errorText}>{error}</Text>}

                {/* Primary Action - Start New */}
                <TouchableOpacity
                  style={styles.primaryButton}
                  onPress={handleStartNew}
                  disabled={isLoading}
                  activeOpacity={0.8}
                  accessibilityLabel={t('home.letsStart')}
                  accessibilityRole="button">
                  {isLoading ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <View style={styles.buttonContent}>
                      <Text style={styles.primaryButtonText}>{t('home.letsStart')}</Text>
                      <Text style={styles.buttonIcon}>→</Text>
                    </View>
                  )}
                </TouchableOpacity>

                {/* Secondary Action - Returning User */}
                <TouchableOpacity
                  style={styles.secondaryButton}
                  onPress={() => setShowLoginOptions(true)}
                  activeOpacity={0.8}
                  accessibilityLabel={t('home.returning')}
                  accessibilityRole="button">
                  <Text style={styles.secondaryButtonText}>{t('home.returning')}</Text>
                </TouchableOpacity>
              </>
            )}

            {showLoginOptions && !showEmailInput && !emailSent && (
              <>
                <Text style={styles.sectionTitle}>{t('home.welcomeBack')}</Text>
                <Text style={styles.sectionDescription}>{t('home.chooseLogin')}</Text>

                {/* QR Code Option */}
                <TouchableOpacity
                  style={styles.optionButton}
                  onPress={() => navigateTo('/auth/qr-scan')}
                  activeOpacity={0.8}
                  accessibilityLabel={`${t('home.scanQr')}. ${t('home.scanQrDescription')}`}
                  accessibilityRole="button">
                  <View style={styles.optionIconContainer}>
                    <Icon name="qr_code_scanner" size={28} color="#fff" />
                  </View>
                  <View style={styles.optionTextContainer}>
                    <Text style={styles.optionTitle}>{t('home.scanQr')}</Text>
                    <Text style={styles.optionDescription}>{t('home.scanQrDescription')}</Text>
                  </View>
                </TouchableOpacity>

                {/* Email Option */}
                <TouchableOpacity
                  style={styles.optionButton}
                  onPress={() => setShowEmailInput(true)}
                  activeOpacity={0.8}
                  accessibilityLabel={`${t('home.useEmail')}. ${t('home.useEmailDescription')}`}
                  accessibilityRole="button">
                  <View style={styles.optionIconContainer}>
                    <Icon name="mail" size={28} color="#fff" />
                  </View>
                  <View style={styles.optionTextContainer}>
                    <Text style={styles.optionTitle}>{t('home.useEmail')}</Text>
                    <Text style={styles.optionDescription}>{t('home.useEmailDescription')}</Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.backButton}
                  onPress={handleBackToStart}
                  activeOpacity={0.8}>
                  <Text style={styles.backButtonText}>← {t('common.back')}</Text>
                </TouchableOpacity>
              </>
            )}

            {showEmailInput && !emailSent && (
              <>
                <Text style={styles.sectionTitle}>{t('home.enterEmail')}</Text>
                <Text style={styles.sectionDescription}>{t('home.sendLink')}</Text>

                <TextInput
                  style={styles.textInput}
                  placeholder="your@email.com"
                  placeholderTextColor="rgba(0,0,0,0.4)"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  editable={!isSendingEmail}
                  accessibilityLabel={t('home.enterEmail')}
                />

                {emailError && <Text style={styles.errorText}>{emailError}</Text>}

                <TouchableOpacity
                  style={[styles.primaryButton, isSendingEmail && styles.buttonDisabled]}
                  onPress={handleSendMagicLink}
                  disabled={isSendingEmail}
                  activeOpacity={0.8}
                  accessibilityLabel={t('home.sendLoginLink')}
                  accessibilityRole="button">
                  {isSendingEmail ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <Text style={styles.primaryButtonText}>{t('home.sendLoginLink')}</Text>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.backButton}
                  onPress={handleBackToStart}
                  activeOpacity={0.8}>
                  <Text style={styles.backButtonText}>← {t('common.back')}</Text>
                </TouchableOpacity>
              </>
            )}

            {emailSent && (
              <>
                <View style={styles.successIconContainer}>
                  <Icon name="check_circle" size={28} color="#fff" />
                </View>
                <Text style={styles.sectionTitle}>{t('home.checkEmail')}</Text>
                <Text style={styles.sectionDescription}>{t('home.emailSent', {email})}</Text>

                <TouchableOpacity
                  style={styles.backButton}
                  onPress={handleBackToStart}
                  activeOpacity={0.8}>
                  <Text style={styles.backButtonText}>← {t('home.startOver')}</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </ScrollView>

      {/* Legal Info Modal */}
      <LegalInfoModal
        visible={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        initialTab={legalModalTab}
        showFaq={true}
      />

      {/* Privacy Banner */}
      <PrivacyBanner
        onOpenPrivacyPolicy={() => {
          setLegalModalTab('privacy');
          setShowLegalModal(true);
        }}
      />

    </View>
    </KeyboardAware>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Simulating gradient with semi-transparent black
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  scrollContent: {
    flexGrow: 1,
    minHeight: '100%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoImage: {
    width: 40,
    height: 40,
    marginRight: spacing.sm,
    borderRadius: 8,
  },
  logoText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  helpButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    // @ts-ignore - web-specific style
    backdropFilter: 'blur(10px)',
  },
  helpButtonText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
  },
  cardContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  card: {
    backgroundColor: 'rgba(255, 250, 245, 0.95)',
    borderRadius: 40,
    padding: spacing.xl,
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
    // @ts-ignore - web-specific style
    backdropFilter: 'blur(20px)',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 10},
    shadowOpacity: 0.3,
    shadowRadius: 30,
    elevation: 20,
  },
  welcomeTitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 28,
    fontWeight: '800',
    color: '#221910',
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    fontWeight: '400',
    color: '#666',
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 24,
  },
  primaryButton: {
    backgroundColor: '#ee8c2b',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.md,
    minHeight: 56,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginBottom: spacing.md,
    shadowColor: '#ee8c2b',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  primaryButtonText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  buttonIcon: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    marginLeft: spacing.sm,
  },
  secondaryButton: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.md,
    minHeight: 56,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    borderWidth: 2,
    borderColor: 'rgba(34, 25, 16, 0.2)',
    backgroundColor: 'transparent',
  },
  secondaryButtonText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    fontWeight: '600',
    color: '#221910',
  },
  sectionTitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 24,
    fontWeight: '700',
    color: '#221910',
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  sectionDescription: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 15,
    fontWeight: '400',
    color: '#666',
    textAlign: 'center',
    marginBottom: spacing.lg,
    lineHeight: 22,
  },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(238, 140, 43, 0.1)',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    width: '100%',
    marginBottom: spacing.sm,
    minHeight: touchTargets.recommended,
  },
  optionIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#ee8c2b',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  optionTextContainer: {
    flex: 1,
  },
  optionTitle: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    fontWeight: '600',
    color: '#221910',
    marginBottom: 2,
  },
  optionDescription: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 13,
    fontWeight: '400',
    color: '#666',
  },
  backButton: {
    paddingVertical: spacing.md,
    marginTop: spacing.sm,
  },
  backButtonText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 14,
    fontWeight: '500',
    color: '#ee8c2b',
  },
  textInput: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: borderRadius.md,
    padding: spacing.md,
    width: '100%',
    marginBottom: spacing.md,
    minHeight: touchTargets.recommended,
    color: '#221910',
  },
  errorText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 14,
    color: '#dc2626',
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
    shadowOpacity: 0,
  },
  successIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#22c55e',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  materialIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 28,
    color: '#fff',
  },
  footerLinks: {
    position: 'absolute',
    bottom: spacing.md,
    right: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  footerLink: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  footerDivider: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 12,
  },
});
